import { Flex, FlexColumnAlignJustifyCenter } from "@/components/Flex";
import { themeColors } from "@/utils/themeColors";
import type { JSX } from "react";

const getSectorsColor = (score: number, customColors?: string[]) => {
    const colors = customColors || ["#FF6363", "#FFAE63", themeColors.primary3, "#09F", themeColors.primary];
    return colors[score - 1] || themeColors.neutral1;
};

const Dividers = ({ count, color }: { count: number; color?: string }) => {
    const dividers: JSX.Element[] = [];
    for (let i = 0; i < count; i++) {
        dividers.push(
            <div
                key={i}
                style={{
                    width: "0.3rem",
                    height: "13rem",
                    background: color ?? themeColors.neutralWhite,
                    transform: `rotate(${i * (180 / (count - 1)) - 90}deg)`,
                    position: "absolute",
                    left: "50%",
                    transformOrigin: "bottom",
                    zIndex: 1,
                }}
            />
        );
    }
    return <>{dividers}</>;
};

const FilledRoot = ({
    score,
    customColors,
    rootColor,
}: {
    score: number;
    customColors?: string[];
    rootColor?: string;
}) => {
    const angle = (score * 180) / 5;
    const fillColor = getSectorsColor(score, customColors);
    const unfilledColor = rootColor || themeColors.neutral1;

    return (
        <div
            style={{
                position: "absolute",
                bottom: 0,
                width: "24rem",
                height: "12rem",
                borderTopLeftRadius: "150px",
                borderTopRightRadius: "150px",
                backgroundImage: `conic-gradient(from ${270}deg at 50% 100%, ${fillColor} 0deg, ${fillColor} ${angle}deg, ${unfilledColor} ${angle}deg, ${unfilledColor} 0deg)`,
            }}
        />
    );
};

type GaugeProps = {
    score: number;
    strokeWidth?: number;
    size?: "small" | "medium";
    customColors?: string[];
    dividersColor?: string;
    rootColor?: string;
    sectorsCount?: number;
};

const ScoreGauge = ({
    score,
    strokeWidth = 8,
    size = "medium",
    sectorsCount = 6,
    customColors,
    dividersColor,
    rootColor,
}: GaugeProps) => {
    const width = size === "small" ? "18rem" : "24rem";
    const height = size === "small" ? "9rem" : "12rem";

    return (
        <FlexColumnAlignJustifyCenter>
            <Flex
                style={{
                    width: width,
                    height: height,
                    backgroundColor: rootColor ?? themeColors.neutral1,
                    borderTopLeftRadius: "150px",
                    borderTopRightRadius: "150px",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    position: "relative",
                    overflow: "hidden",
                }}>
                <Flex
                    style={{
                        position: "relative",
                        width: `calc(${width} - ${strokeWidth}rem)`,
                        height: `calc(${height} - ${strokeWidth / 2}rem)`,
                        background: themeColors.neutralWhite,
                        borderTopLeftRadius: "100px",
                        borderTopRightRadius: "100px",
                        justifyContent: "center",
                        alignItems: "flex-end",
                        zIndex: 2,
                    }}
                />
                <Dividers count={sectorsCount} color={dividersColor} />
                <FilledRoot score={score} customColors={customColors} rootColor={rootColor} />
            </Flex>
            <span className={"heading2"} style={{ marginTop: "-18%", zIndex: 4 }}>
                {score}
            </span>
        </FlexColumnAlignJustifyCenter>
    );
};

export default ScoreGauge;
